'use client';

import { calculateSummary, markDuplicateRequests, type RequestLog, type RequestSummary } from '@/utils/requestTracker';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { QueryKey } from '@/data/enums/queryKeys';
import { useQueryClient } from '@tanstack/react-query';

type RequestTrackerProviderProps = {
    children: ReactNode;
    // note: used for testing only
    initialAppInfo?: AppInfo;
    // note: used for testing only
    initialServerLogs?: RequestLog[];
};

/**
 * Provider component that manages request logs globally using QueryClient for server data
 */
export const RequestTrackerProvider = ({
    children,
    initialAppInfo,
    initialServerLogs,
}: RequestTrackerProviderProps) => {
    const queryClient = useQueryClient();

    // Read data written by setQueryData (e.g. makeFetch.ts) without creating a QueryObserver,
    // which would leave pending Promises alive after unmount and show as async leaks in tests.
    const [queryServerLogs, setQueryServerLogs] = useState<RequestLog[]>(
        () => queryClient.getQueryData<RequestLog[]>([QueryKey.ServerRequests]) ?? []
    );

    const [queryAppInfo, setQueryAppInfo] = useState<AppInfo | undefined>(() =>
        queryClient.getQueryData<AppInfo>([QueryKey.AppInfo])
    );

    useEffect(() => {
        // Read values that were already hydrated into the cache before this subscription
        // was established. On first page load, RequestTrackerProvider (root layout) mounts
        // before HydrationBoundary (main layout) hydrates the cache. By the time this
        // useEffect runs (after render), HydrationBoundary has already populated the cache,
        // so we read it directly here to catch that initial data.
        const existingServerLogs = queryClient.getQueryData<RequestLog[]>([QueryKey.ServerRequests]);
        if (existingServerLogs !== undefined) {
            setQueryServerLogs(existingServerLogs);
        }

        const existingAppInfo = queryClient.getQueryData<AppInfo>([QueryKey.AppInfo]);
        if (existingAppInfo !== undefined) {
            setQueryAppInfo(existingAppInfo);
        }

        return queryClient.getQueryCache().subscribe((event) => {
            if (event.type !== 'updated') {
                return;
            }
            const [key] = event.query.queryKey;
            if (key === QueryKey.ServerRequests) {
                setQueryServerLogs(queryClient.getQueryData<RequestLog[]>([QueryKey.ServerRequests]) ?? []);
            } else if (key === QueryKey.AppInfo) {
                setQueryAppInfo(queryClient.getQueryData<AppInfo>([QueryKey.AppInfo]));
            }
        });
    }, [queryClient]);

    // Use initialServerLogs if provided, otherwise use query data
    const serverLogs = initialServerLogs ?? queryServerLogs;
    const appInfo = initialAppInfo ?? queryAppInfo;

    const [clientLogs, setClientLogs] = useState<RequestLog[]>([]);

    // Listen for client-side API requests
    useEffect(() => {
        const handleApiRequest = (event: CustomEvent<RequestLog>) => {
            setClientLogs((prev) => [...prev, event.detail]);
        };

        window.addEventListener('api-request-completed', handleApiRequest as EventListener);

        return () => {
            window.removeEventListener('api-request-completed', handleApiRequest as EventListener);
        };
    }, []);

    const clearLogs = useCallback(() => {
        setClientLogs([]);
    }, []);

    const serverSummary = useMemo(() => calculateSummary(serverLogs), [serverLogs]);
    const clientSummary = useMemo(() => calculateSummary(clientLogs), [clientLogs]);

    // Mark duplicates across all requests, then split back into server/client.
    const { markedClientLogs, markedServerLogs } = useMemo(() => {
        const markedRequests = markDuplicateRequests([...serverLogs, ...clientLogs]);
        return {
            markedClientLogs: markedRequests.slice(serverLogs.length),
            markedServerLogs: markedRequests.slice(0, serverLogs.length),
        };
    }, [serverLogs, clientLogs]);

    const value = useMemo(
        () => ({
            appInfo,
            clearLogs,
            clientLogs: markedClientLogs,
            clientSummary,
            serverLogs: markedServerLogs,
            serverSummary,
        }),
        [appInfo, clearLogs, markedClientLogs, clientSummary, markedServerLogs, serverSummary]
    );

    return <RequestTrackerContext.Provider value={value}>{children}</RequestTrackerContext.Provider>;
};

RequestTrackerProvider.displayName = 'RequestTrackerProvider';

export type AppInfo = {
    appVersion: string;
};

export type RequestTrackerContextType = {
    appInfo?: AppInfo;
    clearLogs: () => void;
    clientLogs: RequestLog[];
    clientSummary: RequestSummary;
    serverLogs: RequestLog[];
    serverSummary: RequestSummary;
};

export const defaultRequestTrackerContext: RequestTrackerContextType = {
    clearLogs: () => {},
    clientLogs: [],
    clientSummary: { requests: 0, totalTime: 0, waterfalls: 0 },
    serverLogs: [],
    serverSummary: { requests: 0, totalTime: 0, waterfalls: 0 },
};

export const RequestTrackerContext = createContext<RequestTrackerContextType | undefined>(undefined);

/**
 * Hook to access request tracker context
 */
export const useRequestTracker = (): RequestTrackerContextType => {
    const context = useContext(RequestTrackerContext);
    if (context === undefined) {
        throw new Error('useRequestTracker must be used within a RequestTrackerProvider');
    }
    return context;
};
